import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { ZoomableImage } from "components/common/ZoomableImage";
import { Card, CardContent, CardFooter, CardHeader } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Progress } from "components/ui/progress";
import { getCampaignFinancialStats } from "lib/projects";
import { cn } from "lib/utils";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

function formatCampaignDate(date) {
  if (!date) return "—";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "—";

  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  const hours24 = parsed.getHours();
  const ampm = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;

  return `${day}/${month}/${year} ${hours12}:${minutes} ${ampm}`;
}

const statusBadgeBaseClass = "w-full justify-center py-1.5";

const campaignShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  goal: PropTypes.number,
  isSample: PropTypes.bool,
  campaignStartAt: PropTypes.string,
  campaignEndAt: PropTypes.string,
  daysRemaining: PropTypes.number,
  status: PropTypes.string,
});

function CampaignStat({ label, value }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-[11px] leading-snug text-muted-foreground sm:text-xs">{label}</p>
      <p className="wrap-break-word text-xs font-medium leading-snug tabular-nums sm:text-sm">
        {value}
      </p>
    </div>
  );
}

CampaignStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function isCampaignStarted(campaignStartAt) {
  if (!campaignStartAt) return true;
  const startMs = new Date(campaignStartAt).getTime();
  if (Number.isNaN(startMs)) return true;
  return Date.now() >= startMs;
}

function CampaignStatusBadge({ campaignStartAt, daysRemaining, status }) {
  if (!isCampaignStarted(campaignStartAt)) {
    return (
      <Badge variant="secondary" className={statusBadgeBaseClass}>
        Coming Soon
      </Badge>
    );
  }

  const days =
    daysRemaining != null && !Number.isNaN(Number(daysRemaining))
      ? Number(daysRemaining)
      : null;
  const hasEnded = days != null && days <= 0;

  if (hasEnded) {
    return (
      <Badge
        variant="secondary"
        className={cn(statusBadgeBaseClass, "text-muted-foreground")}
      >
        Ended
      </Badge>
    );
  }

  if (status === "closed") {
    return (
      <Badge
        variant="secondary"
        className={cn(statusBadgeBaseClass, "text-muted-foreground")}
      >
        Funding Closed
      </Badge>
    );
  }

  if (days != null && days > 0) {
    return (
      <Badge
        className={cn(
          statusBadgeBaseClass,
          "bg-accent text-accent-foreground"
        )}
      >
        {days} Days Remaining
      </Badge>
    );
  }

  return (
    <Badge variant="secondary" className={statusBadgeBaseClass}>
      Coming Soon
    </Badge>
  );
}

CampaignStatusBadge.propTypes = {
  campaignStartAt: PropTypes.string,
  daysRemaining: PropTypes.number,
  status: PropTypes.string,
};

export function CampaignCard({ campaign, className }) {
  const {
    fundsRaised,
    totalFundsRequired,
    investorCount,
    minimumInvestmentAmount,
    maximumInvestmentAmount,
    sharePriceAmount,
  } = getCampaignFinancialStats(campaign);

  const goal = campaign.goal > 0 ? campaign.goal : 1;
  const percentFunded = Math.min((fundsRaised / goal) * 100, 100);

  return (
    <Link to={`/campaign/${campaign.id}`} className="block h-full w-full min-w-0">
      <Card
        className={cn(
          "group flex h-full w-full min-w-0 flex-col gap-0 overflow-hidden p-0 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg",
          className
        )}
      >
        <div className="relative aspect-video shrink-0 overflow-hidden bg-muted">
          {campaign.isSample ? (
            <Badge
              variant="destructive"
              className="absolute left-3 top-3 z-10 shadow-sm"
            >
              Sample Project
            </Badge>
          ) : null}
          {campaign.image ? (
            <ZoomableImage
              src={campaign.image}
              alt={campaign.title}
              className="block h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              containerClassName="absolute inset-0 h-full w-full"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
              No image
            </div>
          )}
        </div>

        <CardHeader className="min-w-0 space-y-2 px-6 pb-3 pt-4">
          <h3 className="line-clamp-2 text-base font-semibold leading-snug transition-colors group-hover:text-accent sm:text-lg">
            {campaign.title}
          </h3>
          <p className="line-clamp-3 min-h-15 text-sm leading-relaxed text-muted-foreground">
            {campaign.description}
          </p>
        </CardHeader>

        <CardContent className="min-w-0 flex-1 space-y-4 pb-4">
          {campaign.goal > 0 ? (
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-base font-bold wrap-break-word tabular-nums sm:text-lg">
                  {formatCurrency(fundsRaised)}
                </span>
                <span className="text-xs wrap-break-word text-muted-foreground sm:text-sm">
                  of {formatCurrency(campaign.goal)}
                </span>
              </div>
              <Progress value={percentFunded} className="h-3" />
              <p className="mt-2 text-xs text-muted-foreground">
                {percentFunded.toFixed(0)}% funded
              </p>
            </div>
          ) : null}

          <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-x-4 sm:gap-y-3">
            <CampaignStat label="Started" value={formatCampaignDate(campaign.campaignStartAt)} />
            <CampaignStat label="Ended" value={formatCampaignDate(campaign.campaignEndAt)} />
            <CampaignStat
              label="Total funds required"
              value={formatCurrency(totalFundsRequired)}
            />
            <CampaignStat label="Investor count" value={String(investorCount)} />
            <CampaignStat
              label="Min investment"
              value={formatCurrency(minimumInvestmentAmount)}
            />
            <CampaignStat
              label="Max investment"
              value={formatCurrency(maximumInvestmentAmount)}
            />
            <CampaignStat
              label="Share price"
              value={`$${Number(sharePriceAmount).toFixed(2)}`}
            />
          </div>
        </CardContent>

        <CardFooter className="mt-auto border-t px-6 pb-5 pt-0">
          <div className="flex w-full flex-col gap-3 pt-4">
            <CampaignStatusBadge
              campaignStartAt={campaign.campaignStartAt}
              daysRemaining={campaign.daysRemaining}
              status={campaign.status}
            />
          </div>
        </CardFooter>
      </Card>
    </Link>
  );
}

CampaignCard.propTypes = {
  campaign: campaignShape.isRequired,
  className: PropTypes.string,
};
